import React, { useState, useEffect, useCallback } from 'react';
import {
  FiCheck as Check,
  FiX as X,
  FiAlertTriangle as AlertTriangle,
  FiRefreshCw as RefreshCw,
  FiBell as Bell,
  FiMail as Mail,
  FiMessageSquare as Slack,
  FiMonitor as InApp,
  FiAlertCircle as AlertCircle,
  FiDollarSign as DollarSign,
  FiFolder as Folder,
  FiActivity as Activity,
  FiSettings as Settings,
  FiFilter as Filter,
  FiPhoneCall as PagerDuty,
  FiLink as Webhook,
  FiRotateCcw as Reset,
  FiEye as Eye,
  FiUsers as Users,
  FiToggleLeft as ToggleOff,
  FiToggleRight as ToggleOn,
  FiInfo as Info,
  FiShield as Shield,
  FiDatabase as Database,
} from 'react-icons/fi';
import { IconType } from 'react-icons';
import { notificationCatalogAPI } from '../../../services/realApi';
import { useClient } from '../../../contexts/ClientContext';
import Toggle from '../../../components/ui/Toggle';

interface NotificationCatalogProps {
  compact?: boolean;
}

interface CatalogItem {
  id: string;
  code: string;
  type_code: string;
  name: string;
  description?: string;
  category: string;
  severity: string;
  default_severity?: string;
  default_channels: string[];
  channels?: string[];
  is_enabled: boolean;
  default_enabled?: boolean;
  template_subject?: string;
  template_body?: string;
  message_template?: string;
  recipient_roles?: string[];
  cooldown_minutes?: number;
  created_at?: string;
  last_sent_at?: string;
  send_count?: number;
  client_config?: Record<string, unknown> | null;
  email_recipients?: string[];
  supports_external_channels?: boolean;
}

interface QueueStats {
  pending_count?: number;
  processing_count?: number;
  recent_failures?: number;
  pending?: number;
  sentToday?: number;
  failedToday?: number;
  skippedToday?: number;
}

interface CategoryConfigEntry {
  name: string;
  icon: IconType;
  color: string;
}

// Category configuration with icons and descriptions
const CATEGORY_CONFIG: Record<string, CategoryConfigEntry> = {
  funding: {
    name: 'Funding',
    icon: DollarSign,
    color: 'emerald',
  },
  budget: {
    name: 'Budget',
    icon: DollarSign,
    color: 'amber',
  },
  project: {
    name: 'Projects',
    icon: Folder,
    color: 'green',
  },
  alert: {
    name: 'Alerts',
    icon: AlertCircle,
    color: 'red',
  },
  cost: {
    name: 'Cost',
    icon: Activity,
    color: 'blue',
  },
  support: {
    name: 'Support',
    icon: Bell,
    color: 'purple',
  },
  system: {
    name: 'System',
    icon: Settings,
    color: 'gray',
  },
  security: {
    name: 'Security',
    icon: Shield,
    color: 'red',
  },
  dataops: {
    name: 'DataOps',
    icon: Database,
    color: 'blue',
  },
};

// Severity configuration with proper styled badges
const SEVERITY_CONFIG = {
  info: {
    label: 'Info',
    bgColor: 'bg-blue-100 dark:bg-blue-900/40',
    textColor: 'text-blue-700 dark:text-blue-300',
    dotColor: 'bg-blue-500',
  },
  warning: {
    label: 'Warning',
    bgColor: 'bg-amber-100 dark:bg-amber-900/40',
    textColor: 'text-amber-700 dark:text-amber-300',
    dotColor: 'bg-amber-500',
  },
  critical: {
    label: 'Critical',
    bgColor: 'bg-red-100 dark:bg-red-900/40',
    textColor: 'text-red-700 dark:text-red-300',
    dotColor: 'bg-red-500',
  },
  success: {
    label: 'Success',
    bgColor: 'bg-green-100 dark:bg-green-900/40',
    textColor: 'text-green-700 dark:text-green-300',
    dotColor: 'bg-green-500',
  },
};

// Channel icons with explicit Tailwind classes
const CHANNEL_ICONS = {
  in_app: {
    icon: InApp,
    label: 'In-App',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  email: {
    icon: Mail,
    label: 'Email',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-600 dark:text-purple-400',
  },
  slack: {
    icon: Slack,
    label: 'Slack',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
  },
  pagerduty: {
    icon: PagerDuty,
    label: 'PagerDuty',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    iconColor: 'text-orange-600 dark:text-orange-400',
  },
  webhook: {
    icon: Webhook,
    label: 'Webhook',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    iconColor: 'text-gray-600 dark:text-gray-400',
  },
};

/**
 * NotificationCatalog - System notifications configuration section.
 * Displays org-wide notification types with enable/disable toggles and queue stats.
 *
 * @param {boolean} compact - If true, removes standalone header (for use in unified page)
 */
const NotificationCatalog: React.FC<NotificationCatalogProps> = ({ compact = false }) => {
  const { selectedClientId } = useClient();
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedNotification, setSelectedNotification] = useState<CatalogItem | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);

  useEffect(() => {
    loadCatalog();
    loadQueueStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedClientId]);

  const loadCatalog = async () => {
    setLoading(true);
    setError(null);
    try {
      const category = selectedCategory === 'all' ? null : selectedCategory;
      const result = await notificationCatalogAPI.getCatalog(category, selectedClientId);
      if (result.success) {
        setCatalogItems(result.items || []);
        setCategories(result.categories || []);
      } else {
        setError(result.error || 'Failed to load notification catalog');
      }
    } catch (err: unknown) {
      console.error('Error loading catalog:', err);
      setError(err instanceof Error ? err.message : 'Failed to load catalog');
    } finally {
      setLoading(false);
    }
  };

  const loadQueueStats = async () => {
    try {
      const result = await notificationCatalogAPI.getQueueStats(selectedClientId);
      if (result.success) {
        setQueueStats(result);
      }
    } catch (err: unknown) {
      console.error('Error loading queue stats:', err);
    }
  };

  const handleToggleEnabled = async (item: CatalogItem, enabled: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const result = await notificationCatalogAPI.updateClientConfig(item.code, {
        is_enabled: enabled,
      }, selectedClientId);
      if (result.success) {
        await loadCatalog();
        setSuccess(`${item.name} ${enabled ? 'enabled' : 'disabled'}`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to update notification');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update notification');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfig = async (config: Record<string, unknown>) => {
    setSaving(true);
    setError(null);
    try {
      if (!selectedNotification) return;
      const result = await notificationCatalogAPI.updateClientConfig(selectedNotification.code, config, selectedClientId);
      if (result.success) {
        await loadCatalog();
        setShowConfigModal(false);
        setSelectedNotification(null);
        setSuccess('Configuration saved successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to save configuration');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleResetConfig = async (item: CatalogItem) => {
    setSaving(true);
    setError(null);
    try {
      const result = await notificationCatalogAPI.resetClientConfig(item.code, selectedClientId);
      if (result.success) {
        await loadCatalog();
        setShowConfigModal(false);
        setSelectedNotification(null);
        setSuccess(`${item.name} reset to defaults`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to reset configuration');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reset configuration');
    } finally {
      setSaving(false);
    }
  };

  // Batch enable/disable all notifications
  const handleBatchToggle = useCallback(async (enableAll: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const itemsToUpdate = catalogItems.filter(item => item.is_enabled !== enableAll);
      for (const item of itemsToUpdate) {
        await notificationCatalogAPI.updateClientConfig(item.code, { is_enabled: enableAll }, selectedClientId);
      }
      await loadCatalog();
      setSuccess(`${enableAll ? 'Enabled' : 'Disabled'} ${itemsToUpdate.length} notifications`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update notifications');
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogItems, loadCatalog]);

  const openConfigModal = (item: CatalogItem) => {
    setSelectedNotification(item);
    setShowConfigModal(true);
  };

  const getCategoryColor = (category: string): string => {
    const config = CATEGORY_CONFIG[category];
    const colorMap = {
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      gray: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
    };
    return colorMap[config?.color as keyof typeof colorMap] || colorMap.gray;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  // Group items by category
  const groupedItems = catalogItems.reduce<Record<string, CatalogItem[]>>((acc, item) => {
    const category = item.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  if (loading && catalogItems.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Success Banner */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
            <p className="text-green-800 dark:text-green-300">{success}</p>
          </div>
        </div>
      )}

      {/* Header - only shown when not in compact mode */}
      {!compact && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Notification Catalog
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Configure which notifications your organization receives and how they are delivered
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="text-sm pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_CONFIG[cat]?.name || cat}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => { loadCatalog(); loadQueueStats(); }}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Compact mode toolbar */}
      {compact && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="text-sm pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer focus:outline-none"
                aria-label="Filter by category"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_CONFIG[cat]?.name || cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Batch Actions */}
            <div className="flex items-center gap-1 border-l border-gray-200 dark:border-gray-600 pl-3">
              <button
                onClick={() => handleBatchToggle(true)}
                disabled={saving || loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
                aria-label="Enable all notifications"
              >
                <ToggleOn className="w-3.5 h-3.5" />
                Enable All
              </button>
              <button
                onClick={() => handleBatchToggle(false)}
                disabled={saving || loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
                aria-label="Disable all notifications"
              >
                <ToggleOff className="w-3.5 h-3.5" />
                Disable All
              </button>
            </div>
          </div>

          <button
            onClick={() => { loadCatalog(); loadQueueStats(); }}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            aria-label="Refresh notifications"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      )}

      {/* Queue Stats - Compact inline view */}
      {queueStats && (
        <div className="flex items-center gap-4 px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 text-sm mb-4">
          <span className="text-gray-500 dark:text-gray-400 font-medium flex items-center gap-1.5">
            <Info className="w-4 h-4" />
            Queue Status:
          </span>
          <div className="flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              <span className="text-gray-600 dark:text-gray-300">{queueStats.pending} pending</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span className="text-gray-600 dark:text-gray-300">{queueStats.sentToday} sent</span>
            </span>
            {(queueStats.failedToday ?? 0) > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-red-600 dark:text-red-400">{queueStats.failedToday} failed</span>
              </span>
            )}
            {(queueStats.skippedToday ?? 0) > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                <span className="text-gray-500 dark:text-gray-400">{queueStats.skippedToday} skipped</span>
              </span>
            )}
          </div>
        </div>
      )}


      {/* Catalog Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Notification
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Severity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Channels
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Recipients
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Last Sent
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Count (30d)
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Enabled
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Configure
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {Object.entries(groupedItems).map(([category, items]) => (
              <React.Fragment key={category}>
                {/* Category Header */}
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td colSpan={8} className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const config = CATEGORY_CONFIG[category];
                        const Icon = config?.icon || Bell;
                        return (
                          <>
                            <span className={`p-2 rounded-lg ${getCategoryColor(category)}`}>
                              <Icon className="w-4 h-4" />
                            </span>
                            <div>
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {config?.name || category}
                              </span>
                              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                {items.length} {items.length === 1 ? 'notification' : 'notifications'}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
                {/* Category Items */}
                {items.map((item) => {
                  const emailSuffix = (item.email_recipients?.length ?? 0) > 1 ? 's' : '';
                  const emailRecipientsLabel = (item.email_recipients?.length ?? 0) > 0
                    ? `${item.email_recipients!.length} email${emailSuffix}`
                    : 'All users';
                  return (
                  <tr
                    key={item.code}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      item.is_enabled ? '' : 'opacity-60'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.description?.substring(0, 60)}
                          {(item.description?.length ?? 0) > 60 ? '...' : ''}
                        </div>
                        {item.client_config && (
                          <span className="inline-flex items-center mt-1 text-xs text-blue-600 dark:text-blue-400">
                            <Settings className="w-3 h-3 mr-1" />
                            Custom config
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const severity = SEVERITY_CONFIG[item.default_severity as keyof typeof SEVERITY_CONFIG] || SEVERITY_CONFIG.info;
                        return (
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${severity.bgColor} ${severity.textColor}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${severity.dotColor}`}></span>
                            {severity.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {(item.channels || []).map((channel) => {
                          const channelInfo = CHANNEL_ICONS[channel as keyof typeof CHANNEL_ICONS];
                          if (!channelInfo) return null;
                          const Icon = channelInfo.icon;
                          return (
                            <span
                              key={channel}
                              className={`p-1.5 rounded-md ${channelInfo.bgColor}`}
                              title={channelInfo.label}
                            >
                              <Icon className={`w-3.5 h-3.5 ${channelInfo.iconColor}`} />
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                        <Users className="w-4 h-4" />
                        {emailRecipientsLabel}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(item.last_sent_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">
                      {item.send_count || 0}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Toggle
                        checked={item.is_enabled}
                        onChange={(enabled) => handleToggleEnabled(item, enabled)}
                        label={`${item.is_enabled ? 'Disable' : 'Enable'} ${item.name} notifications`}
                        disabled={saving}
                        size="sm"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => openConfigModal(item)}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Configure notification"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );})}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {catalogItems.length === 0 && !loading && (
          <div className="text-center py-12">
            <Bell className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No notifications found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No notification definitions are available for the selected category.
            </p>
          </div>
        )}
      </div>

      {/* Configuration Modal */}
      {showConfigModal && selectedNotification && (
        <ConfigurationModal
          notification={selectedNotification}
          onSave={handleSaveConfig}
          onReset={() => handleResetConfig(selectedNotification)}
          onClose={() => { setShowConfigModal(false); setSelectedNotification(null); }}
          saving={saving}
        />
      )}
    </div>
  );
};

interface ConfigurationModalProps {
  notification: CatalogItem;
  onSave: (config: Record<string, unknown>) => void;
  onReset: () => void;
  onClose: () => void;
  saving: boolean;
}

// Configuration Modal Component
const ConfigurationModal: React.FC<ConfigurationModalProps> = ({ notification, onSave, onReset, onClose, saving }) => {
  const [isEnabled, setIsEnabled] = useState(notification.is_enabled ?? notification.default_enabled);
  const [channels, setChannels] = useState(notification.channels || notification.default_channels || []);
  const [emailRecipients, setEmailRecipients] = useState(notification.email_recipients || []);
  const [useAllUsers, setUseAllUsers] = useState(!notification.email_recipients || notification.email_recipients.length === 0);
  const [newEmail, setNewEmail] = useState('');

  const availableChannels = ['in_app', 'email', 'slack', 'pagerduty', 'webhook'];
  const hasCustomConfig = notification.client_config !== null;

  const handleChannelToggle = (channel: string) => {
    if (channels.includes(channel)) {
      setChannels(channels.filter((c) => c !== channel));
    } else {
      setChannels([...channels, channel]);
    }
  };

  const handleAddEmail = () => {
    const email = newEmail.trim();
    if (email && !emailRecipients.includes(email) && email.includes('@')) {
      setEmailRecipients([...emailRecipients, email]);
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (email: string) => {
    setEmailRecipients(emailRecipients.filter((e) => e !== email));
  };

  const handleSave = () => {
    onSave({
      is_enabled: isEnabled,
      channels,
      email_recipients: useAllUsers ? null : emailRecipients,
    });
  };

  // Render template preview
  const renderPreview = () => {
    let template = notification.message_template || 'No template available';
    // Replace placeholders with sample values
    template = template.replaceAll(/\{\{(\w+)\}\}/g, (_match, key) => {
      const samples = {
        application_name: 'AWS Credits Q1',
        project_name: 'Demo Project',
        days_until_expiration: '7',
        expiration_date: '2025-03-15',
        amount: '10,000.00',
        percentage: '85',
        current_spend: '8,500.00',
        budget_limit: '10,000.00',
        recommendation_count: '5',
        environment_name: 'Production',
        rule_name: 'High Latency Alert',
        message: 'Latency exceeded threshold',
      };
      return (samples as Record<string, string>)[key] || `[${key}]`;
    });
    return template;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity" onClick={onClose} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }} role="presentation" />

        <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Configure: {notification.name}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Description */}
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {notification.description}
            </div>

            {/* Enable/Disable */}
            <div className="space-y-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable this notification
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsEnabled(true)}
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                    isEnabled
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Enabled
                </button>
                <button
                  onClick={() => setIsEnabled(false)}
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                    isEnabled
                      ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      : 'border-gray-500 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Disabled
                </button>
              </div>
            </div>

            {/* Delivery Channels */}
            <div className="space-y-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Delivery Channels
              </span>
              <div className="space-y-2">
                {availableChannels.map((channel) => {
                  const channelInfo = CHANNEL_ICONS[channel as keyof typeof CHANNEL_ICONS];
                  const Icon = channelInfo?.icon || Bell;
                  const isActive = channels.includes(channel);
                  const isSupported = ['in_app', 'email'].includes(channel) || notification.supports_external_channels;

                  return (
                    <button
                      key={channel}
                      onClick={() => isSupported && handleChannelToggle(channel)}
                      disabled={!isSupported}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                        isSupported
                          ? isActive
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
                          : 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
                      }`}
                    >
                      <span className={`p-2 rounded-lg ${isActive ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-gray-100 dark:bg-gray-600'}`}>
                        <Icon className={`w-4 h-4 ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
                      </span>
                      <div className="flex-1">
                        <div className={`text-sm font-medium ${isActive ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
                          {channelInfo?.label || channel}
                        </div>
                        {!isSupported && (
                          <div className="text-xs text-gray-500">Requires integration setup</div>
                        )}
                      </div>
                      {isActive && <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Email Recipients */}
            {channels.includes('email') && (
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email Recipients
                </span>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setUseAllUsers(true)}
                      className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                        useAllUsers
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <Users className="w-4 h-4 inline-block mr-2" />
                      All users
                    </button>
                    <button
                      onClick={() => setUseAllUsers(false)}
                      className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                        useAllUsers
                          ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          : 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      }`}
                    >
                      <Mail className="w-4 h-4 inline-block mr-2" />
                      Specific emails
                    </button>
                  </div>

                  {!useAllUsers && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                          placeholder="Enter email address"
                          className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500"
                        />
                        <button
                          onClick={handleAddEmail}
                          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Add
                        </button>
                      </div>
                      {emailRecipients.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {emailRecipients.map((email) => (
                            <span
                              key={email}
                              className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
                            >
                              {email}
                              <button
                                onClick={() => handleRemoveEmail(email)}
                                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Preview */}
            <div className="space-y-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <Eye className="w-4 h-4 inline-block mr-1" />
                Message Preview
              </span>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {renderPreview()}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <div>
              {hasCustomConfig && (
                <button
                  onClick={onReset}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                >
                  <Reset className="w-4 h-4" />
                  Reset to defaults
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save Configuration
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCatalog;
